import { useEffect, useState, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/StatusBadge";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { MessageSquare, MapPin, User, Tag, Calendar, Paperclip, Download, Pencil, RotateCcw, Archive, X, Check, Trash2 } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleDateString();
}

export default function TicketDetailSheet({ ticketId, open, onOpenChange, onChanged }) {
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");

  const isStaff = user?.role === "staff" || user?.role === "admin";
  const isAdmin = user?.role === "admin";
  const canManage = ["admin", "it", "maintenance", "custodian", "mechanic", "secretary", "principal"].includes(user?.role);
  const canAssign = canManage;
  const canEdit = canManage;
  const canReopen = canManage && ticket && (ticket.status === "Resolved" || ticket.status === "Closed" || ticket.archived_year);
  const isTicketOwner = ticket?.requester_id === user?.id;
  const canTeacherClose = (user?.role === "user" || user?.role === "teacher") && isTicketOwner &&
    ticket?.status !== "Resolved" && ticket?.status !== "Closed";

  const load = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/tickets/${ticketId}`);
      setTicket(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    if (open && ticketId) {
      load();
      if (canManage) api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    }
  }, [open, ticketId, canManage, load]);

  const patch = async (payload) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/tickets/${ticketId}`, payload);
      setTicket(data);
      onChanged?.();
      toast.success("Updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = () => {
    setEditTitle(ticket?.title || "");
    setEditDesc(ticket?.description || "");
    setEditing(true);
  };
  const cancelEdit = () => setEditing(false);
  const saveEdit = async () => {
    const title = editTitle.trim();
    const description = editDesc.trim();
    if (!title || !description) {
      toast.error("Title and description cannot be empty");
      return;
    }
    await patch({ title, description });
    setEditing(false);
  };

  const reopen = async () => {
    if (!confirm("Re-open this ticket? It will move back to the live queue with status Open.")) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/tickets/${ticketId}/reopen`);
      setTicket(data);
      onChanged?.();
      toast.success("Ticket reopened");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const removeTicket = async () => {
    if (!ticket) return;
    const label = `"${ticket.title}" (${ticket.site} · ${ticket.department} · ${ticket.priority})`;
    if (!confirm(`Permanently DELETE ticket ${label}?\n\nThis cannot be undone. A full snapshot will be written to the audit log for verification.`)) return;
    setSaving(true);
    try {
      await api.delete(`/tickets/${ticketId}`);
      toast.success("Ticket deleted and logged to audit trail");
      onChanged?.();
      onOpenChange?.(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const submitComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/tickets/${ticketId}/comments`, { body: comment });
      setComment("");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadAtt = async (i) => {
    try {
      const { data } = await api.get(`/tickets/${ticketId}/attachments/${i}`);
      const a = document.createElement("a");
      a.href = data.data;
      a.download = data.name;
      a.click();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Assignees must belong to the same department as the ticket.
  // Users with no department (teachers, secretaries, principals, plain users) are never assignable.
  const assignableStaff = ticket?.department
    ? users.filter((u) => u.department === ticket.department)
    : [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto bg-white" data-testid="ticket-detail-sheet">
        {/* Always-present a11y title — visible SheetTitle below overrides visually.
            Prevents Radix `DialogContent requires a DialogTitle` warning during
            loading / editing states when the visible SheetTitle isn't rendered. */}
        <SheetTitle className="sr-only">{ticket?.title || "Ticket details"}</SheetTitle>
        {loading || !ticket ? (
          <div className="p-8 text-sm text-zinc-500">Loading…</div>
        ) : (
          <>
            <SheetHeader>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-xs text-zinc-500 font-mono">#{ticket.id.slice(0, 8)}</div>
                {canEdit && !editing && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={startEdit}
                    data-testid="ticket-edit-button"
                    className="h-8 text-xs text-zinc-600 hover:text-zinc-950"
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                  </Button>
                )}
              </div>
              {editing ? (
                <div className="space-y-2 mt-1">
                  <Input
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    data-testid="ticket-edit-title-input"
                    className="font-heading text-lg"
                    placeholder="Title"
                  />
                </div>
              ) : (
                <SheetTitle className="font-heading text-2xl text-zinc-950">{ticket.title}</SheetTitle>
              )}
              <div className="flex flex-wrap gap-2 pt-1">
                <Badge kind="status" value={ticket.status} />
                <Badge kind="priority" value={ticket.priority} />
                <Badge kind="dept" value={ticket.department} />
                {ticket.archived_year && (
                  <span data-testid="detail-archived-badge" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-full">
                    <Archive className="h-2.5 w-2.5" /> Archived · {ticket.archived_year}
                  </span>
                )}
              </div>
            </SheetHeader>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              {/* Main */}
              <div className="md:col-span-2 space-y-6">
                <section className="border border-zinc-200 rounded-md p-5 bg-white">
                  <div className="text-xs uppercase tracking-[0.05em] text-zinc-500 mb-2">Description</div>
                  {editing ? (
                    <div className="space-y-3">
                      <Textarea
                        value={editDesc}
                        onChange={(e) => setEditDesc(e.target.value)}
                        rows={6}
                        data-testid="ticket-edit-description-input"
                        className="rounded-md"
                        placeholder="Description"
                      />
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={cancelEdit}
                          data-testid="ticket-edit-cancel"
                          className="h-9"
                        >
                          <X className="h-3.5 w-3.5 mr-1" /> Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={saveEdit}
                          disabled={saving}
                          data-testid="ticket-edit-save"
                          className="h-9 bg-[#0055FF] hover:bg-[#0040CC] text-white"
                        >
                          <Check className="h-3.5 w-3.5 mr-1" /> Save changes
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed">{ticket.description}</p>
                  )}
                </section>

                {ticket.attachments?.length > 0 && (
                  <section className="border border-zinc-200 rounded-md p-5">
                    <div className="text-xs uppercase tracking-[0.05em] text-zinc-500 mb-3">Attachments</div>
                    <ul className="space-y-2">
                      {ticket.attachments.map((a, i) => (
                        <li key={`${a.name}-${a.size ?? ""}-${i}`} className="flex items-center justify-between text-sm bg-zinc-50 rounded px-3 py-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <Paperclip className="h-4 w-4 text-zinc-500" />
                            <span className="truncate">{a.name}</span>
                          </div>
                          <button
                            onClick={() => downloadAtt(i)}
                            data-testid={`attachment-download-${i}`}
                            className="text-[#0055FF] hover:text-[#0040CC] flex items-center gap-1 text-xs"
                          >
                            <Download className="h-3 w-3" /> Download
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                <section className="border border-zinc-200 rounded-md p-5">
                  <div className="text-xs uppercase tracking-[0.05em] text-zinc-500 mb-3 flex items-center gap-2">
                    <MessageSquare className="h-3.5 w-3.5" /> Activity
                  </div>
                  <div className="space-y-3">
                    {(ticket.comments || []).length === 0 && (
                      <div className="text-sm text-zinc-500">No comments yet.</div>
                    )}
                    {(ticket.comments || []).map((c) => (
                      <div key={c.id} className="border-l-2 border-zinc-200 pl-3">
                        <div className="text-xs text-zinc-500">
                          <span className="font-medium text-zinc-800">{c.author_name}</span>
                          <span className="mx-1">·</span>
                          <span>{timeAgo(c.created_at)}</span>
                          <span className="ml-1 text-[10px] uppercase tracking-wider text-zinc-400">{c.author_role}</span>
                        </div>
                        <div className="text-sm text-zinc-800 mt-1 whitespace-pre-wrap">{c.body}</div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 space-y-2">
                    <Textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      rows={3}
                      placeholder="Add a comment…"
                      data-testid="comment-input"
                      className="rounded-md focus-visible:ring-2 focus-visible:ring-blue-500/20"
                    />
                    <div className="flex justify-end">
                      <Button
                        onClick={submitComment}
                        disabled={!comment.trim()}
                        data-testid="comment-submit-button"
                        className="bg-[#0055FF] hover:bg-[#0040CC] text-white transition-colors duration-200"
                      >
                        Post reply
                      </Button>
                    </div>
                  </div>
                </section>
              </div>

              {/* Metadata sidebar */}
              <aside className="space-y-4">
                <div className="border border-zinc-200 rounded-md p-4 space-y-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 mb-1">Requester</div>
                    <div className="flex items-center gap-2 text-zinc-800"><User className="h-4 w-4 text-zinc-400" />{ticket.requester_name}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{ticket.requester_email}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 mb-1">Category</div>
                    <div className="flex items-center gap-2 text-zinc-800"><Tag className="h-4 w-4 text-zinc-400" />{ticket.category}</div>
                  </div>
                  {ticket.location && (
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 mb-1">Location</div>
                      <div className="flex items-center gap-2 text-zinc-800"><MapPin className="h-4 w-4 text-zinc-400" />
                        <span>
                          {ticket.location_type && <span className="text-zinc-500">{ticket.location_type} · </span>}
                          {ticket.location}
                        </span>
                      </div>
                    </div>
                  )}
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 mb-1">Created</div>
                    <div className="flex items-center gap-2 text-zinc-800"><Calendar className="h-4 w-4 text-zinc-400" />{timeAgo(ticket.created_at)}</div>
                  </div>
                </div>

                {canManage && (
                  <div className="border border-zinc-200 rounded-md p-4 space-y-4" data-testid="manage-panel">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 mb-2">Status</div>
                      <Select value={ticket.status} onValueChange={(v) => patch({ status: v })} disabled={saving}>
                        <SelectTrigger data-testid="detail-status-trigger"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["Open", "In Progress", "Resolved", "Closed"].map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 mb-2">Priority</div>
                      <Select value={ticket.priority} onValueChange={(v) => patch({ priority: v })} disabled={saving}>
                        <SelectTrigger data-testid="detail-priority-trigger"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["Low","Medium","High","Critical"].map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    {canAssign && (
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 mb-2">Assignee</div>
                      <Select
                        value={ticket.assignee_id || "unassigned"}
                        onValueChange={(v) => patch({ assignee_id: v === "unassigned" ? null : v })}
                        disabled={saving}
                      >
                        <SelectTrigger data-testid="detail-assignee-trigger"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {assignableStaff.map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.name} {u.department ? `(${u.department})` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    )}
                    {canReopen && (
                      <div className="pt-2 border-t border-zinc-100">
                        <Button
                          onClick={reopen}
                          disabled={saving}
                          data-testid="ticket-reopen-button"
                          variant="outline"
                          className="w-full h-10 rounded-md border-amber-300 text-amber-800 hover:bg-amber-50 bg-white"
                        >
                          <RotateCcw className="h-3.5 w-3.5 mr-2" /> Re-open ticket
                        </Button>
                        {ticket.archived_year && (
                          <p className="text-[10px] text-zinc-500 mt-1.5 leading-snug">Reopening will move this ticket out of the {ticket.archived_year} archive back into the live queue.</p>
                        )}
                      </div>
                    )}
                    {isAdmin && (
                      <div className="pt-3 mt-1 border-t border-zinc-100 dark:border-zinc-800">
                        <div className="text-[10px] uppercase tracking-[0.12em] text-red-600 dark:text-red-400 font-semibold mb-1.5 flex items-center gap-1.5">
                          <Trash2 className="h-3 w-3" /> Danger zone
                        </div>
                        <Button
                          onClick={removeTicket}
                          disabled={saving}
                          data-testid="ticket-delete-button"
                          variant="outline"
                          className="w-full h-11 rounded-md bg-red-50 border-red-300 text-red-800 hover:bg-red-100 hover:border-red-500 hover:text-red-900 dark:bg-red-950/40 dark:border-red-900 dark:text-red-200 dark:hover:bg-red-950/70 dark:hover:border-red-700 font-semibold text-[13px] transition-colors px-2 tracking-tight justify-start"
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1 text-red-600 dark:text-red-400 shrink-0" strokeWidth={2.5} />
                          Delete permanently
                        </Button>
                        <p className="text-[10px] text-zinc-500 dark:text-zinc-400 mt-2 leading-snug">Admin-only. A full snapshot (title, requester, dept, site, status, priority, timestamps) is written to the audit log before the ticket is removed.</p>
                      </div>
                    )}
                  </div>
                )}
                {canTeacherClose && (
                  <div className="border border-emerald-200 rounded-md p-4 bg-emerald-50" data-testid="teacher-close-panel">
                    <div className="text-[10px] uppercase tracking-[0.1em] text-emerald-800 mb-2">Problem solved?</div>
                    <p className="text-xs text-emerald-900/80 mb-3 leading-relaxed">
                      If your issue was resolved, mark this ticket so the team knows they can close the loop.
                    </p>
                    <div className="flex flex-col gap-2">
                      <Button
                        onClick={() => patch({ status: "Resolved" })}
                        disabled={saving}
                        data-testid="teacher-mark-resolved"
                        className="w-full h-10 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium whitespace-nowrap transition-colors duration-200"
                      >
                        Mark resolved
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => patch({ status: "Closed" })}
                        disabled={saving}
                        data-testid="teacher-mark-closed"
                        className="w-full h-10 rounded-md border-emerald-300 text-emerald-800 hover:bg-emerald-100 hover:text-emerald-900 bg-white text-sm font-medium whitespace-nowrap transition-colors duration-200"
                      >
                        Close ticket
                      </Button>
                    </div>
                  </div>
                )}
              </aside>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
